// Native ↔ WebView live session link (WS-LINK, plan §5 / g3 session-bridge-audit).
//
// The WebView→native write direction already works for free: supabase-js's `secureStorageAdapter`
// (src/integrations/supabase/secureStorageAdapter.ts) is the client's ONLY storage backend on native,
// so every internal session mutation (sign-in, its own autoRefreshToken timer, refreshSession(),
// sign-out) already lands in the same `CapacitorStorage` SharedPreferences file/key that
// BriefAutoRepository.java reads and writes natively (g3 §1). That half is NOT re-implemented here.
//
// The missing half — and the only thing this module does — is:
//   1. On app `resume`, re-read the on-disk session and adopt it via `supabase.auth.setSession()` if
//      it is newer than what the WebView currently holds in memory (native rotated the token while
//      the WebView slept — e.g. Android Auto refreshed it in the background).
//   2. Give `useAuth.ts` a small helper to consult before/after its own `refreshSession()` calls, so a
//      losing refresh (rejected because the other process already redeemed the single-use refresh
//      token) adopts the winner's freshly-written disk session instead of hard-signing-out the user
//      (g3 §4 G2 — "the single most important correctness fix").
//
// Native-only: every export here is a no-op (or resolves false/null) on web, so the web bundle and
// web build behaviour are unaffected. Nothing here adds a second `onAuthStateChange` subscription —
// useAuth.ts's existing one (useAuth.ts:35) remains the sole subscriber, per plan §5.

import { Preferences } from '@capacitor/preferences';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { isNative } from '@/lib/native/platform';

// Matches src/integrations/supabase/client.ts's SUPABASE_URL project ref, and the native side's
// `PROJECT_REF` constant (BriefAutoRepository.java:86) — both must keep resolving to this same key.
const PROJECT_REF = 'ewyfhrjexfrmcjhqzsmm';
const SESSION_KEY = `sb-${PROJECT_REF}-auth-token`;

// Mirrors the native 30s REFRESH_GUARD_MS window (BriefAutoRepository.java) so the two processes'
// "someone already refreshed recently" guards agree — see plan §5 rotation-race guard.
const REFRESH_SKEW_SEC = 60;

// The subset of the supabase-js persisted session shape this module reads/compares/adopts. Only
// fields we actually touch — the full shape is passed through untouched to setSession() via the
// parsed object's access_token/refresh_token.
interface PersistedSession {
  access_token: string;
  refresh_token: string;
  expires_at?: number; // unix seconds
  token_type?: string;
  user?: unknown;
}

function isPersistedSession(value: unknown): value is PersistedSession {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.access_token === 'string' && typeof v.refresh_token === 'string';
}

/**
 * Read + parse the on-disk session. Returns null on any missing/unparseable/malformed value —
 * shape-tolerant by design, since the native side may have written a slightly different shape
 * (g3 §4 G3) and a parse failure here must never throw or crash the WebView.
 */
async function readDiskSession(): Promise<PersistedSession | null> {
  try {
    const { value } = await Preferences.get({ key: SESSION_KEY });
    if (!value) return null;
    const parsed = JSON.parse(value);
    // supabase-js persists either the session object directly, or (older/edge shapes) wrapped as
    // { currentSession: {...} }. Accept either.
    const candidate =
      isPersistedSession(parsed) ? parsed :
      isPersistedSession((parsed as { currentSession?: unknown })?.currentSession)
        ? (parsed as { currentSession: PersistedSession }).currentSession
        : null;
    return candidate;
  } catch {
    return null;
  }
}

/** True if `a`'s expiry is meaningfully newer than `b`'s (or `b` is absent/expiry-less). */
function isNewer(a: PersistedSession, b: { expires_at?: number | null } | null | undefined): boolean {
  if (!b || typeof b.expires_at !== 'number') return true;
  if (typeof a.expires_at !== 'number') return false;
  return a.expires_at > b.expires_at;
}

/**
 * Resume reconciliation (g3 §4 G1). Call from the app `resume` hook point (useReconnectDrain).
 * Re-reads disk; if it holds a session newer than the in-memory one (by refresh_token identity or a
 * later expires_at), adopts it via setSession() BEFORE any refresh/sign-out decision runs — so a
 * native-rotated token (e.g. Android Auto refreshing while the WebView was backgrounded) is picked
 * up instead of being clobbered by the WebView's own stale in-memory refresh_token.
 * No-op on web. Never throws.
 */
export async function reconcileSessionOnResume(): Promise<void> {
  if (!isNative()) return;
  try {
    const disk = await readDiskSession();
    if (!disk) return;

    const { data: { session: memory } } = await supabase.auth.getSession();

    const diskDiffersFromMemory =
      !memory || memory.refresh_token !== disk.refresh_token || isNewer(disk, memory);

    if (!diskDiffersFromMemory) return;
    if (!isNewer(disk, memory) && memory) return; // same-or-older; don't regress a valid in-memory session

    const { error } = await supabase.auth.setSession({
      access_token: disk.access_token,
      refresh_token: disk.refresh_token,
    });
    if (error) {
      // Disk copy didn't validate (e.g. malformed shape per g3 G3, or genuinely stale) — leave the
      // in-memory session as-is; the existing refresh/sign-out paths in useAuth.ts still run normally.
      console.warn('🔗 sessionBridge: resume adopt failed, keeping in-memory session', error.message);
      return;
    }
    console.log('🔗 sessionBridge: adopted newer on-disk session on resume');
  } catch (err) {
    console.warn('🔗 sessionBridge: resume reconciliation error (non-fatal)', err);
  }
}

/**
 * Rotation-race guard, pre-check (g3 §4 G2 item 1). Call before the WebView issues its own manual
 * `refreshSession()`. If disk already shows an `expires_at` more than REFRESH_SKEW_SEC in the future
 * vs the in-memory session, someone else (native) already rotated — adopt the disk copy and report
 * `true` so the caller skips its own refresh call instead of racing to redeem the same refresh token.
 * Returns false (do the normal refresh) on web, on any error, or when disk is not meaningfully ahead.
 */
export async function adoptDiskSessionIfFresh(currentExpiresAt: number | null | undefined): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const disk = await readDiskSession();
    if (!disk || typeof disk.expires_at !== 'number') return false;

    const ahead =
      typeof currentExpiresAt === 'number'
        ? disk.expires_at - currentExpiresAt
        : Number.POSITIVE_INFINITY;
    if (ahead <= REFRESH_SKEW_SEC) return false;

    const { error } = await supabase.auth.setSession({
      access_token: disk.access_token,
      refresh_token: disk.refresh_token,
    });
    return !error;
  } catch {
    return false;
  }
}

/**
 * Rotation-race guard, post-rejection (g3 §4 G2 item 2 — "the single most important correctness
 * fix"). Call when a `refreshSession()` call has just failed. Re-reads disk fresh; if it holds a
 * valid, non-expired session (a concurrent native refresh won the race and wrote it moments ago),
 * adopts it via setSession() and returns true so the caller does NOT hard sign-out. Returns false
 * (caller should proceed to sign-out) when disk is empty/unparseable/also-expired, or on web.
 */
export async function adoptDiskSessionAfterRefreshRejection(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const disk = await readDiskSession();
    if (!disk) return false;

    const now = Math.floor(Date.now() / 1000);
    if (typeof disk.expires_at === 'number' && disk.expires_at <= now) return false; // disk is also stale

    const { error } = await supabase.auth.setSession({
      access_token: disk.access_token,
      refresh_token: disk.refresh_token,
    });
    if (error) return false;
    console.log('🔗 sessionBridge: adopted disk session after a losing refresh race — skipping sign-out');
    return true;
  } catch {
    return false;
  }
}
